import React, { useEffect, useMemo, useRef, useState } from 'react';
import reportsAPI from '../api/reportsAPI';

const DEFAULT_SUGGESTIONS = [
  'Show me my P&L',
  'Tell me about my February performance',
  'Why is my net income negative',
  'Compare Feb vs Jan',
  'Top expenses this month',
  'Show expenses for Feb grouped by category',
  'Cash flow this month'
];

const MAX_MESSAGES = 30;
const THINKING_DELAY_MS = 350;

const buildIntroMessage = (username) => ({
  id: `intro-${username || 'user'}`,
  role: 'assistant',
  text: `Hi ${username || 'there'}, I'm Spark, your accounting insights assistant.\n\nI read your books and summarize the numbers for you.\n\nWhat would you like to know today?\n\nYou can ask about profit, expenses, cash flow, or performance.`
});

const formatNumber = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getStorageKey = (accountingClientId) => `aiReportsChat:${accountingClientId || 'unknown'}`;

const normalizeMessages = (messages) => {
  if (!Array.isArray(messages)) return [];
  return messages.slice(-MAX_MESSAGES);
};

const AiReportsChatDrawer = ({ isOpen, onClose, accountingClientId, username }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState(DEFAULT_SUGGESTIONS);
  const endRef = useRef(null);

  const storageKey = useMemo(() => getStorageKey(accountingClientId), [accountingClientId]);

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (!saved) {
      setMessages([buildIntroMessage(username)]);
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      const normalized = normalizeMessages(parsed);
      if (!normalized.length) {
        setMessages([buildIntroMessage(username)]);
        return;
      }
      setMessages(normalized);
    } catch (error) {
      setMessages([buildIntroMessage(username)]);
    }
  }, [storageKey, username]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(normalizeMessages(messages)));
  }, [messages, storageKey]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen, loading]);

  const appendMessage = (message) => {
    setMessages((prev) => normalizeMessages([...prev, message]));
  };

  const handleSend = async (rawText) => {
    const messageText = (rawText || input).trim();
    if (!messageText || loading) return;

    if (!accountingClientId) {
      appendMessage({
        id: Date.now(),
        role: 'assistant',
        text: 'Select an accounting company first, then try again.'
      });
      setInput('');
      return;
    }

    appendMessage({ id: Date.now(), role: 'user', text: messageText });
    setInput('');
    setLoading(true);

    try {
      const [result] = await Promise.all([
        reportsAPI.reportsChat({
          accountingClientId,
          message: messageText,
          timezone: 'Asia/Manila'
        }),
        new Promise((resolve) => setTimeout(resolve, THINKING_DELAY_MS))
      ]);

      const responsePayload = result?.response || {};
      const interpreted = result?.interpreted || {};

      appendMessage({
        id: Date.now() + 1,
        role: 'assistant',
        response: responsePayload,
        text: responsePayload?.title || 'Here is your report summary.'
      });

      if (Array.isArray(responsePayload?.suggestedQuestions) && responsePayload.suggestedQuestions.length) {
        setSuggestedQuestions(responsePayload.suggestedQuestions.slice(0, 5));
      } else {
        setSuggestedQuestions(DEFAULT_SUGGESTIONS);
      }
    } catch (error) {
      appendMessage({
        id: Date.now() + 2,
        role: 'assistant',
        text: typeof error === 'string' ? error : 'Failed to process your question. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    handleSend();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed z-50 bottom-24 left-2 right-2 sm:left-auto sm:right-4 sm:w-[360px]">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-teal-600 text-white">
          <div>
            <h3 className="font-semibold text-sm">VentaHub Spark</h3>
            <p className="text-xs text-teal-100">Real-time answers from your books</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white text-lg leading-none px-2 py-1 rounded hover:bg-teal-700"
            aria-label="Close AI Reports Chat"
          >
            ×
          </button>
        </div>

        <div className="h-80 overflow-y-auto p-3 space-y-3 bg-gray-50">
          {messages.length === 0 && (
            <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded-lg p-3">
              Ask about performance, period comparisons, expenses, or cash flow.
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                  message.role === 'user'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-800 border border-gray-200'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.text}</p>

                {message.role === 'assistant' && Array.isArray(message?.response?.blocks) && (
                  <div className="mt-2 space-y-2">
                    {message.response.blocks.map((block, idx) => {
                      if (block.type === 'kpis') {
                        const entries = Object.entries(block.data || {});
                        return (
                          <div key={`${message.id}-kpi-${idx}`} className="grid grid-cols-1 gap-1 text-xs">
                            {entries.map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="text-gray-500 capitalize">{key}</span>
                                <span className="font-medium">{formatNumber(value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }

                      if (block.type === 'bullets') {
                        return (
                          <div key={`${message.id}-bullets-${idx}`}>
                            {block.title ? <p className="text-xs font-semibold mb-1">{block.title}</p> : null}
                            <ul className="list-disc pl-4 text-xs space-y-1">
                              {(block.items || []).map((item, itemIdx) => (
                                <li key={`${message.id}-bullet-${idx}-${itemIdx}`}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      }

                      if (block.type === 'table') {
                        return (
                          <div key={`${message.id}-table-${idx}`} className="overflow-x-auto border border-gray-200 rounded">
                            <table className="min-w-full text-xs">
                              <thead className="bg-gray-100">
                                <tr>
                                  {(block.columns || []).map((col) => (
                                    <th key={`${message.id}-${col}-${idx}`} className="px-2 py-1 text-left font-medium">
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(block.rows || []).slice(0, 20).map((row, rowIdx) => (
                                  <tr key={`${message.id}-row-${idx}-${rowIdx}`} className="border-t border-gray-100">
                                    {row.map((cell, cellIdx) => (
                                      <td key={`${message.id}-cell-${idx}-${rowIdx}-${cellIdx}`} className="px-2 py-1">
                                        {typeof cell === 'number' ? formatNumber(cell) : cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }

                      return null;
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-2 inline-block">
              Reviewing your numbers…
            </div>
          )}

          <div ref={endRef} />
        </div>

        <div className="border-t border-gray-200 p-3 bg-white">
          <div className="flex flex-wrap gap-2 mb-3">
            {suggestedQuestions.slice(0, 4).map((question) => (
              <button
                key={question}
                type="button"
                onClick={() => handleSend(question)}
                className="text-xs px-2 py-1 bg-teal-50 text-teal-700 rounded-full border border-teal-100 hover:bg-teal-100"
              >
                {question}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask a reports question"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-teal-600 text-white px-3 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AiReportsChatDrawer;
